import React from "react";
import { Link } from "react-router-dom";
import { MapPin, IndianRupee, Briefcase, Star, Calendar, Zap, GraduationCap } from "lucide-react";
import StarRating from "./StarRating";

const typeBadge = {
  "Walk-in": "bg-orange-50 text-orange-700 border-orange-200",
  "Daily Wage": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function JobCard({ job }) {
  // Mirror JobDetail.jsx logic: rely on salary_type, not show_salary.
  const salary = (() => {
    // Explicitly hidden, or no salary info at all → not disclosed
    if (job.salary_type === "hidden" || (!job.salary_type && !job.show_salary))
      return "Not disclosed";
    // salary_type set but no actual values entered → not disclosed
    if (!job.salary_min && !job.salary_max) return "Not disclosed";
    if (job.salary_type === "yearly")
      return `₹${(job.salary_min || 0).toLocaleString()} - ₹${(job.salary_max || 0).toLocaleString()} LPA`;
    return `₹${(job.salary_min || 0).toLocaleString()} - ₹${(job.salary_max || 0).toLocaleString()}`;
  })();

  const fresherBadge = job.fresher_option === "freshers_only"
    ? "Only freshers can apply"
    : job.fresher_option === "freshers_can_apply"
      ? "Freshers can apply"
      : null;

  return (
    <Link
      to={`/jobs/${job.id}`}
      data-testid={`job-card-${job.id}`}
      className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md hover:border-[#1D6FF2]/40 transition-all duration-200 flex flex-col gap-4 group"
    >
      <div className="flex justify-between items-start gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {job.featured && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Featured
              </span>
            )}
            {job.urgency === "urgent" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-200">
                <Zap className="w-3 h-3" /> Urgent Hiring
              </span>
            )}
            {job.urgency === "immediate" && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-200">
                <Zap className="w-3 h-3" /> Immediate Hiring
              </span>
            )}
          </div>
          <h3 className="mt-2 text-lg font-semibold text-slate-900 group-hover:text-[#1D6FF2]">{job.title}</h3>
          <p className="text-sm text-slate-600 flex items-center gap-1">
            <Briefcase className="w-3.5 h-3.5" /> {job.business_name}
          </p>
          {job.company_reviews_count > 0 && (
            <div className="mt-1"><StarRating value={job.company_rating || 0} count={job.company_reviews_count} size="w-3.5 h-3.5" /></div>
          )}
        </div>
        {fresherBadge && (
          <span className="shrink-0 text-[10px] font-semibold px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 whitespace-nowrap">
            {fresherBadge}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
        <span className="flex items-center gap-1 text-slate-600"><MapPin className="w-4 h-4 text-slate-400" /> {job.city}</span>
        <span className="flex items-center gap-1 text-slate-900 font-semibold"><IndianRupee className="w-4 h-4" /> {salary}</span>
        {job.experience && (
          <span className="flex items-center gap-1 text-slate-500"><GraduationCap className="w-4 h-4" /> {job.experience}</span>
        )}
        <span className="flex items-center gap-1 text-slate-500"><Calendar className="w-4 h-4" /> {(job.created_at || "").slice(0,10)}</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="badge-category text-[11px] font-medium px-2.5 py-0.5 rounded-full">{job.category}</span>
            <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full border ${typeBadge[job.job_type] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
              {job.job_type}
            </span>
      </div>

      <div className="pt-3 mt-auto border-t border-slate-100 flex justify-between items-center text-sm">
        {/* <span className="text-slate-500">{job.applications_count || 0} applicants</span> */}
        <span className="text-[#1D6FF2] font-medium group-hover:underline">View Details →</span>
      </div>
    </Link>
  );
}